class RecentlyViewedBar extends HTMLElement {
  constructor() {
    super();
    this.currentPage = 0;
    this.itemsPerSlide = 2;
  }

  connectedCallback() {
    const excludeId = this.hasAttribute('exclude-product-id')
      ? parseInt(this.getAttribute('exclude-product-id'))
      : null;

    this.items = this.getItems(excludeId);
    if (this.items.length === 0) return;

    if (this.isMobile) {
      this.renderMobile();
    } else {
      this.renderDesktop();
    }

    this.injectNavCounter();
  }

  get isMobile() {
    return window.innerWidth <= 699;
  }

  getItems(excludeId) {
    try {
      const data = JSON.parse(localStorage.getItem('theme:recently-viewed-data') || '[]');
      return excludeId ? data.filter(item => item.id !== excludeId) : data;
    } catch (e) {
      return [];
    }
  }

  /* ── Desktop ─────────────────────────────────────────────── */
  renderDesktop() {
    const label = this.dataset.label || 'Recently viewed';
    const closeLabel = this.dataset.closeLabel || 'Close';
    const clearLabel = this.dataset.clearLabel || 'Clear';
    const displayItems = this.items.slice(0, 6);

    this.innerHTML = `
      <div class="rvb__panel">
        <div class="rvb__inner">
          <p class="rvb__title">${label} (${this.items.length})</p>
          <div class="rvb__products">
            ${displayItems.map(item => `
              <a href="${item.url}" class="rvb__item">
                <img src="${item.image}" alt="${item.title}" loading="lazy" width="60" height="60">
                <div class="rvb__tooltip">
                  <img src="${item.image}" alt="${item.title}" loading="lazy">
                  <p class="rvb__tooltip-title">${item.title}</p>
                  <p class="rvb__tooltip-price">${item.price || ''}</p>
                </div>
              </a>
            `).join('')}
          </div>
          <button type="button" class="rvb__clear tap-area" aria-label="${clearLabel}">${clearLabel}</button>
          <button type="button" class="rvb__close tap-area" aria-label="${closeLabel}">&times;</button>
        </div>
      </div>
    `;

    this.classList.add('rvb--hidden');

    this.querySelector('.rvb__close').addEventListener('click', () => {
      this.classList.add('rvb--hidden');
      const trigger = document.querySelector('.rvb__nav-trigger');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    });

    this.querySelector('.rvb__clear').addEventListener('click', () => this.clearHistory());
  }

  /* ── Mobile: inject overlay + drawer directly on body ─────── */
  renderMobile() {
    const label = this.dataset.label || 'Recently viewed';
    const closeLabel = this.dataset.closeLabel || 'Close';
    const clearLabel = this.dataset.clearLabel || 'Clear';
    const displayItems = this.items.slice(0, 6);
    const totalPages = Math.ceil(displayItems.length / this.itemsPerSlide);

    // Overlay
    this._overlay = document.createElement('div');
    this._overlay.className = 'rvb__overlay rvb--hidden';
    this._overlay.addEventListener('click', () => this.closeMobile());
    document.body.appendChild(this._overlay);

    // Drawer
    this._drawer = document.createElement('div');
    this._drawer.className = 'rvb__drawer rvb--hidden';
    this._drawer.innerHTML = `
      <div class="rvb__drawer-header">
        <p class="rvb__title">${label} (${this.items.length})</p>
        <button type="button" class="rvb__clear tap-area" aria-label="${clearLabel}">${clearLabel}</button>
        <button type="button" class="rvb__close tap-area" aria-label="${closeLabel}">&times;</button>
      </div>
      <div class="rvb__slider-wrapper">
        <button type="button" class="rvb__arrow rvb__prev tap-area" aria-label="Previous">&#8249;</button>
        <div class="rvb__track-outer">
          <div class="rvb__track">
            ${Array.from({ length: totalPages }, (_, i) => {
              const pageItems = displayItems.slice(i * this.itemsPerSlide, (i + 1) * this.itemsPerSlide);
              return `<div class="rvb__slide">
                ${pageItems.map(item => `
                  <a href="${item.url}" class="rvb__slide-item">
                    <div class="rvb__slide-item-img" role="img" aria-label="${item.title}" style="background-image: url('${item.image}')"></div>
                    <p class="rvb__slide-title">${item.title}</p>
                    <p class="rvb__slide-price">${item.price || ''}</p>
                  </a>
                `).join('')}
              </div>`;
            }).join('')}
          </div>
        </div>
        <button type="button" class="rvb__arrow rvb__next tap-area" aria-label="Next">&#8250;</button>
      </div>
    `;

    if (totalPages > 1) this._drawer.classList.add('rvb--has-peek');

    document.body.appendChild(this._drawer);

    this._drawer.querySelector('.rvb__close').addEventListener('click', () => this.closeMobile());
    this._drawer.querySelector('.rvb__clear').addEventListener('click', () => this.clearHistory());
    this._drawer.querySelector('.rvb__prev').addEventListener('click', () => this.goToPage(this.currentPage - 1));
    this._drawer.querySelector('.rvb__next').addEventListener('click', () => this.goToPage(this.currentPage + 1));

    this.updateSlider();
  }

  clearHistory() {
    localStorage.removeItem('theme:recently-viewed-data');
    localStorage.removeItem('theme:recently-viewed-products');
    // Remove nav trigger
    const trigger = document.querySelector('.rvb__nav-trigger');
    if (trigger) trigger.remove();
    // Remove overlay + drawer (mobile) or hide bar (desktop)
    if (this._overlay) this._overlay.remove();
    if (this._drawer) this._drawer.remove();
    this.remove();
  }

  closeMobile() {
    if (this._overlay) this._overlay.classList.add('rvb--hidden');
    if (this._drawer) this._drawer.classList.add('rvb--hidden');
    const trigger = document.querySelector('.rvb__nav-trigger');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
  }

  goToPage(page) {
    const totalPages = Math.ceil(Math.min(this.items.length, 6) / this.itemsPerSlide);
    this.currentPage = Math.max(0, Math.min(page, totalPages - 1));
    this.updateSlider();
  }

  updateSlider() {
    if (!this._drawer) return;
    const track = this._drawer.querySelector('.rvb__track');
    const slide = this._drawer.querySelector('.rvb__slide');

    if (track) {
      const slides = this._drawer.querySelectorAll('.rvb__slide');
      // Measure stride = distance between slide starts (slide width + gap)
      const stride = slides.length > 1
        ? slides[1].offsetLeft - slides[0].offsetLeft
        : slide?.offsetWidth || 0;
      track.style.transform = stride
        ? `translateX(-${this.currentPage * stride}px)`
        : `translateX(-${this.currentPage * 100}%)`;
    }

    const totalPages = Math.ceil(Math.min(this.items.length, 6) / this.itemsPerSlide);
    const prevBtn = this._drawer.querySelector('.rvb__prev');
    const nextBtn = this._drawer.querySelector('.rvb__next');
    if (prevBtn) prevBtn.disabled = this.currentPage === 0;
    if (nextBtn) nextBtn.disabled = this.currentPage === totalPages - 1;
  }

  /* ── Nav counter ─────────────────────────────────────────── */
  injectNavCounter() {
    if (document.querySelector('.rvb__nav-trigger')) return;

    const label = this.dataset.label || 'Recently viewed';
    const trigger = document.createElement('button');
    trigger.className = 'header__icon-wrapper tap-area rvb__nav-trigger';
    trigger.setAttribute('aria-label', label);
    trigger.setAttribute('aria-expanded', 'false');
    trigger.innerHTML = `<span class="rvb__nav-count">&#8226;&nbsp;(${this.items.length})</span>`;

    trigger.addEventListener('click', () => {
      if (this.isMobile) {
        const hidden = this._overlay?.classList.toggle('rvb--hidden');
        this._drawer?.classList.toggle('rvb--hidden');
        trigger.setAttribute('aria-expanded', hidden ? 'false' : 'true');
      } else {
        const isHidden = this.classList.toggle('rvb--hidden');
        trigger.setAttribute('aria-expanded', isHidden ? 'false' : 'true');
      }
    });

    const iconList = document.querySelector('.header__icon-list');
    if (iconList) iconList.prepend(trigger);
  }
}

window.customElements.define('recently-viewed-bar', RecentlyViewedBar);
